'use client';

import clsx from 'clsx';

import { useState } from 'react';

import { Checkmark, ChevronDown, ChevronUp, Reset } from '@carbon/icons-react';
import { Tag } from '@carbon/react';

import { HorizontalSeparator } from '@/components/HorizontalSeparator';

import styles from './filter-group.module.scss';

export interface FilterGroupLabel {
  text: string;
  disabled?: boolean;
}

interface FilterGroup {
  title: string;
  labels: FilterGroupLabel[];
}

export interface FilterGroupObservable {
  reset?: () => void;
  filter?: (labels: FilterGroupLabel[]) => void;
}

export class FilterGroupObserver {
  private observables: FilterGroupObservable[];
  constructor() {
    this.observables = [];
  }

  public add(observable: FilterGroupObservable) {
    this.observables.push(observable);
  }

  public setFilter(filters: FilterGroupLabel[]) {
    for (const observable of this.observables) {
      if (observable.filter) {
        observable.filter(filters);
      }
    }
  }

  public reset() {
    for (const observable of this.observables) {
      if (observable.reset) {
        observable.reset();
      }
    }
  }
}

export interface Props {
  groups: FilterGroup[];
  multiselect?: boolean;
  filterGroupObserver?: FilterGroupObserver;
  onFilter: (filters: FilterGroupLabel[]) => void;
}

export function FilterGroup({ groups, multiselect = true, onFilter, filterGroupObserver }: Props) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedLabels, setSelectedLabels] = useState<{ [key: string]: FilterGroupLabel }>({});
  const numberOfSelectedLabels = Object.keys(selectedLabels).length;

  if (filterGroupObserver) {
    filterGroupObserver.add({
      reset: () => {
        setSelectedLabels({});
        onFilter([]);
      },
      filter: (filters: FilterGroupLabel[]) => {
        const filtersFound = findFilters(filters, groups);
        setSelectedLabels(filtersFound);
        onFilter(Object.values(filtersFound));
      },
    });
  }

  function isSelected(labelId: string) {
    return !!selectedLabels[labelId];
  }

  function toggleLabel(labelId: string, label: FilterGroupLabel) {
    if (label.disabled) {
      return;
    }

    setSelectedLabels((prev) => {
      let newSelectedLabels = { ...prev };

      if (newSelectedLabels[labelId]) {
        delete newSelectedLabels[labelId];
      } else if (!multiselect) {
        newSelectedLabels = { [labelId]: label };
      } else {
        newSelectedLabels[labelId] = label;
      }

      onFilter(Object.values(newSelectedLabels));

      return newSelectedLabels;
    });
  }

  function clearFilter() {
    setSelectedLabels({});
    onFilter([]);
  }

  function toggleDropdown() {
    setDropdownOpen((prev) => !prev);
  }

  return (
    <div className={styles['filter-group']}>
      <HorizontalSeparator className={styles['filter-group__separator']} />
      <div className={styles['filter-group__header']}>
        <span className={styles['filter-group__header__filter-counter']}>
          {numberOfSelectedLabels > 1 ? 'Filters' : 'Filter'}{' '}
          {multiselect && numberOfSelectedLabels > 0 && (
            <span className={styles['filter-group__header__filter-counter__number']}>
              ({numberOfSelectedLabels})
            </span>
          )}
        </span>
        <span
          className={clsx(styles['filter-group__header__link'], {
            [styles['filter-group__header__link--enabled']]: numberOfSelectedLabels > 0,
          })}
          onClick={() => clearFilter()}
        >
          Clear <Reset size={16} />
        </span>
      </div>
      <div className={styles['filter-group__dropdown']} onClick={toggleDropdown}>
        <span className={styles['filter-group__dropdown__filter-tag']}>
          {numberOfSelectedLabels > 0 && <Tag type="high-contrast">{numberOfSelectedLabels}</Tag>}
          {numberOfSelectedLabels === 0 ? 'Filters' : 'Selected'}
        </span>
        {!dropdownOpen && <ChevronDown />}
        {dropdownOpen && <ChevronUp />}
      </div>
      <ul
        className={clsx(styles['filter-group__list'], {
          [styles['filter-group__list--open']]: dropdownOpen,
        })}
      >
        {groups.map((group) => {
          const groupId = `${group.title}`;

          return (
            <li key={groupId}>
              <span className={styles['filter-group__list__title']}>{group.title}</span>
              <ul className={styles['filter-group__list__labels']}>
                {group.labels.map((label) => {
                  const labelId = getLabelId(label.text, group.title);
                  const selected = isSelected(labelId);

                  return (
                    <li
                      key={labelId}
                      className={clsx(styles['filter-group__list__labels__item'], {
                        [styles['filter-group__list__labels__item--selected']]: selected,
                      })}
                    >
                      <Tag
                        onClick={() => toggleLabel(labelId, label)}
                        type={selected ? 'high-contrast' : 'outline'}
                        id={labelId}
                        filter={selected}
                        disabled={label.disabled}
                        className={clsx(styles['filter-group__list__labels__label'], {
                          [styles['filter-group__list__labels__label--not-filtered']]: !selected,
                        })}
                      >
                        {label.text}
                      </Tag>
                      {selected && (
                        <Checkmark
                          width={16}
                          height={16}
                          className={styles['filter-group__list__labels__item__checkmark']}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function getLabelId(label: string, group: string) {
  return `${group}-${label}`;
}

function findFilters(
  filters: FilterGroupLabel[],
  groups: FilterGroup[]
): { [key: string]: FilterGroupLabel } {
  const formattedFilters: { [key: string]: FilterGroupLabel } = {};
  for (const group of groups) {
    for (const label of group.labels) {
      for (const filter of filters) {
        if (label.text === filter.text) {
          const labelId = getLabelId(label.text, group.title);
          formattedFilters[labelId] = label;
        }
      }
    }
  }

  return formattedFilters;
}
